// --- Métodos para buscar nomes de ficheiros já existentes de cultura ---
/**
 * Busca o nome do ficheiro do mapa de sectores para uma cultura.
 * @param {number|string} cultureId
 * @returns {Promise<string|null>} Nome do ficheiro ou null
 */
export const fetchMapaSectoresFileName = async (cultureId) => {
  try {
    // Adiciona um timestamp para evitar cache
    const timestamp = new Date().getTime();
    const response = await fetch(
      `${API_URL}/api/cultures/${cultureId}/mapa-sectores-filename?t=${timestamp}`,
      {
        credentials: 'include',
        cache: 'no-store', // Força a não usar cache
      }
    );
    if (!response.ok)
      throw new Error('Erro ao buscar nome do ficheiro de mapa de sectores');
    const data = await response.json();
    return data && data.fileName ? data.fileName : null;
  } catch (err) {
    console.error('Erro ao buscar nome do ficheiro de mapa de sectores:', err);
    return null;
  }
};

/**
 * Busca o nome do ficheiro de outros documentos para uma cultura.
 * @param {number|string} cultureId
 * @returns {Promise<string|null>} Nome do ficheiro ou null
 */
export const fetchOtherDocsFileName = async (cultureId) => {
  try {
    // Adiciona um timestamp para evitar cache
    const timestamp = new Date().getTime();
    const response = await fetch(
      `${API_URL}/api/cultures/${cultureId}/other-docs-filename?t=${timestamp}`,
      {
        credentials: 'include',
        cache: 'no-store', // Força a não usar cache
      }
    );
    if (!response.ok)
      throw new Error('Erro ao buscar nome do ficheiro de outros documentos');
    const data = await response.json();
    return data && data.fileName ? data.fileName : null;
  } catch (err) {
    console.error('Erro ao buscar nome do ficheiro de outros documentos:', err);
    return null;
  }
};
const API_URL = process.env.REACT_APP_API_URL;

async function callApi(endpoint, method = 'GET', data = null) {
  const options = {
    method,
    credentials: 'include', // This ensures cookies are included
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    mode: 'cors',
  };

  if (data) {
    options.body = JSON.stringify(data);
  }

  const response = await fetch(`${API_URL}${endpoint}`, options);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

export const login = (credentials) =>
  callApi('/api/authenticate/', 'POST', credentials);
export const logout = () => callApi('/api/logout/', 'POST');
export const register = (userData) =>
  callApi('/api/register/', 'POST', userData);
export const getProfile = () => callApi('/auth/profile');
export const updateProfile = (data) => callApi('/auth/profile', 'PUT', data);

// Add any other API endpoints you need here following the same pattern

// Pilares and Grupos
export const fetchData_getAllPilar = () => callApi('/api/pilar');
export const fetchData_getAllGrupo = () => callApi('/api/grupo');
export const fetchData_getPilar_Questionario = (pilarId, culturaType) =>
  callApi(`/api/pilar-data/?pilar_id=${pilarId}&cultura_type=${culturaType}`);

// Questionnaire and User Management
export const postQuestionnaireResponses = (payload) =>
  callApi('/api/save-responses/', 'POST', payload);

export const updateUserData = (userData) =>
  callApi('/api/update_user/', 'PUT', userData);

export const refreshUserData = () => callApi('/api/auth/status');

// New method to refresh complete user info from the backend
export const refreshUserInfo = () => callApi('/api/user/refresh');

// Alternative method to get fresh user profile data
export const getUserProfile = () => callApi('/auth/profile');

export const authenticateUser = (email, password) =>
  callApi('/api/authenticate/', 'POST', { email, password });

export const registerUser = (email, password) =>
  callApi('/api/register/', 'POST', { email, password });

// Explorations and Cultures
export const saveExplorationData = (explorationData) =>
  explorationData.id
    ? callApi(`/api/exploracoes/${explorationData.id}/`, 'PUT', explorationData)
    : callApi('/api/exploracoes/', 'POST', explorationData);

export const fetchExplorationById = (explorationId) =>
  callApi(`/api/exploracoes/${explorationId}/`, 'GET');

export const saveCultureData = async (cultureData) => {
  const url =
    cultureData instanceof FormData && cultureData.get('id')
      ? `/api/culturas/${cultureData.get('id')}/`
      : '/api/culturas/';

  // Extract ID from FormData if present
  let cultureDataId = null;
  if (cultureData instanceof FormData) {
    cultureDataId = cultureData.get('id');
  } else if (cultureData.id) {
    cultureDataId = cultureData.id;
  }

  const options = {
    method: cultureDataId ? 'PUT' : 'POST',
    credentials: 'include',
    body:
      cultureData instanceof FormData
        ? cultureData
        : JSON.stringify(cultureData),
    headers:
      cultureData instanceof FormData
        ? {} // Let browser set correct multipart boundary
        : { 'Content-Type': 'application/json' },
  };

  const response = await fetch(`${API_URL}${url}`, options);
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
};

export const fetchExploracoes = (userId) =>
  callApi(`/api/refresh_exploracoes/?user_id=${userId}`);

// Choices/Options Endpoints
export const getTipoCulturaChoices = () =>
  callApi('/api/tipo-cultura-choices/');

export const getTipoProducaoChoices = () =>
  callApi('/api/tipo-producao-choices/');

export const getTipoSistemaRegaChoices = () =>
  callApi('/api/tipo-sistema-rega-choices/');

export const getClasseTexturalChoices = () =>
  callApi('/api/classe-textural-solo-choices/');

export const getCaracteristicasAnoChoices = () =>
  callApi('/api/caracteristicas-ano-choices/');

/**
 * Fetch the available ESTADOS choices from the backend.
 * @returns {Promise<Array>} - List of ESTADOS.
 */
export const getEstadosChoices = () => callApi('/api/estados-choices/');

// Certification Related
export const createCertification = (data) =>
  callApi('/api/certificacoes/', 'POST', data);

export const getCertificationName = (certificationId) =>
  callApi(`/api/certification-name/?certification_id=${certificationId}`);

export const getExploracaoAndCulturaFromCertificacao = async (
  certificacaoId
) => {
  const response = await callApi(`/api/certificacoes/${certificacaoId}/`);

  if (!response.exploracao_id || !response.cultura_id) {
    throw new Error('Exploracao ID or Cultura ID not found in the response');
  }

  return {
    exploracao_id: response.exploracao_id,
    cultura_id: response.cultura_id,
    data_creacao_certificacao: response.data_creacao_certificacao,
  };
};

export const getRespostas = (certificadoId) =>
  callApi(`/api/respostas-cert/?certificado_id=${certificadoId}`);

export const checkAuthStatus = () => callApi('/api/auth/status');

// Remove sessionStorage utilities and directly use the refreshToken endpoint
export const refreshToken = async () => {
  try {
    const response = await callApi('/api/token/refresh/', 'POST');
    return response;
  } catch (error) {
    throw new Error('Failed to refresh token');
  }
};

export const verifyUser = async (token) => {
  // console.log('verifyUser called with token:', token);

  // Try only the most likely approach first - URL parameter
  const urlWithToken = `${API_URL}/api/verify/?token=${encodeURIComponent(
    token
  )}`;
  // console.log('Trying URL with parameter:', urlWithToken);

  const options = {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    mode: 'cors',
  };

  try {
    const response = await fetch(urlWithToken, options);
    // console.log('Response status:', response.status);
    // console.log('Response ok:', response.ok);

    if (!response.ok) {
      let errorMessage;
      try {
        const errorData = await response.json();
        // console.log('Error response data:', errorData);
        errorMessage =
          errorData.error ||
          errorData.message ||
          `HTTP error! status: ${response.status}`;
      } catch (parseError) {
        // console.log('Failed to parse error response:', parseError);
        errorMessage = `HTTP error! status: ${response.status}`;
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    // console.log('Verification successful:', result);
    return result;
  } catch (error) {
    console.error('Verification failed:', error);
    throw error;
  }
};

// Profile and Auth
export const uploadProfilePhoto = async (file) => {
  const formData = new FormData();
  formData.append('profile_photo', file);

  const response = await fetch(`${API_URL}/api/uploadProfilePhoto/`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  return url;
};

export const getProfilePhoto = async (userId) => {
  const response = await fetch(`${API_URL}/api/profile-photo/${userId}/`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  return url;
};

export const uploadPdfFile = async (file) => {
  const formData = new FormData();
  formData.append('ifap_pdf', file);

  const response = await fetch(`${API_URL}/api/uploadPdfFile/`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  return url;
};

export const uploadWaterMapFile = async (file) => {
  const formData = new FormData();
  formData.append('water_map', file);

  const response = await fetch(`${API_URL}/api/uploadWaterMap/`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  return url;
};

export const uploadOtherDocs = async (file) => {
  const formData = new FormData();
  formData.append('other_doc', file);

  const response = await fetch(`${API_URL}/api/uploadOtherDoc/`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  return url;
};

// Upload water map file for a specific culture
export const uploadWaterMapFileForCulture = async (file, cultureId) => {
  const formData = new FormData();
  formData.append('mapa_setores_rega', file);
  formData.append('cultura_id', cultureId);

  const response = await fetch(
    `${API_URL}/api/cultures/${cultureId}/upload-water-map/`,
    {
      method: 'POST',
      credentials: 'include',
      body: formData,
    }
  );
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return await response.json();
};

// Upload other documents file for a specific culture
export const uploadOtherDocsFileForCulture = async (file, cultureId) => {
  const formData = new FormData();
  formData.append('outros_documentos', file);
  formData.append('cultura_id', cultureId);

  const response = await fetch(
    `${API_URL}/api/cultures/${cultureId}/upload-other-docs/`,
    {
      method: 'POST',
      credentials: 'include',
      body: formData,
    }
  );
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return await response.json();
};

// Get water map PDF file for a specific culture
export const getWaterMapPdfFile = async (cultureId) => {
  // Adiciona um timestamp para evitar cache
  const timestamp = new Date().getTime();
  const response = await fetch(
    `${API_URL}/api/cultures/${cultureId}/water-map-file/?t=${timestamp}`,
    {
      credentials: 'include',
      cache: 'no-store', // Força a não usar cache
    }
  );
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const blob = await response.blob();

  if (blob.type === 'application/pdf') {
    const url = URL.createObjectURL(blob);
    return { url, isPdf: true, filename: `mapa-sectores-${cultureId}.pdf` };
  } else {
    return { url: null, isPdf: false, filename: null };
  }
};

// Get other documents PDF file for a specific culture
export const getOtherDocsPdfFile = async (cultureId) => {
  // Adiciona um timestamp para evitar cache
  const timestamp = new Date().getTime();
  const response = await fetch(
    `${API_URL}/api/cultures/${cultureId}/other-docs-file/?t=${timestamp}`,
    {
      credentials: 'include',
      cache: 'no-store', // Força a não usar cache
    }
  );
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const blob = await response.blob();

  if (blob.type === 'application/pdf') {
    const url = URL.createObjectURL(blob);
    return { url, isPdf: true, filename: `outros-documentos-${cultureId}.pdf` };
  } else {
    return { url: null, isPdf: false, filename: null };
  }
};

export const getIFAP_PdfFile = async (userId) => {
  const response = await fetch(`${API_URL}/api/ifap-file/${userId}/`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const blob = await response.blob();

  if (blob.type === 'application/pdf') {
    const url = URL.createObjectURL(blob);
    return { url, isPdf: true, filename: `ifap-document-${userId}.pdf` };
  } else {
    const url = URL.createObjectURL(blob);
    return { url, isPdf: false };
  }
};

export const setSubmittedCertificate = async (
  certificationId,
  exploracao_id,
  cultura_id,
  dateCreated
) => {
  const response = await callApi(`/api/certificacoes/${certificationId}/`);

  if (response.estado !== 'PENDENTE') {
    throw new Error('Certificate is not in PENDENTE state');
  }

  const payload = {
    estado: 'SUBMETIDO',
    exploracao_id,
    cultura_id,
    data_creacao_certificacao: dateCreated,
  };

  try {
    return await callApi(
      `/api/certificacoes/${certificationId}/`,
      'PUT',
      payload
    );
  } catch (error) {
    console.error('Error updating certification:', error);
    throw new Error(error.message || 'Failed to update certification');
  }
};

/**
 * Generate a PDF for a specific culture.
 * @param {number} certId - The ID of the culture.
 * @returns {Promise<string>} - The URL of the generated PDF.
 */
export const generatePdfForCulture = async (certId) => {
  const response = await fetch(`${API_URL}/api/certificacao/${certId}/pdf`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to generate PDF. Status: ${response.status}`);
  }

  const blob = await response.blob();
  return URL.createObjectURL(blob); // Return the URL of the generated PDF
};

export const getPilarBestScores = async (culturaType) => {
  const response = await callApi(
    `/api/pilar-scores-sum/?cultura_type=${culturaType}`,
    'GET'
  );

  if (!response) {
    throw new Error('No scores found for the specified Pilar and Cultura Type');
  }

  return response;
};
